import { createColumnDefs, createMapWithTableRows, defaultColumnDefs } from '../helpers/utils';
import { createTableRows, ninetiethPercentileCellStyleRenderer } from '../../../utils/agGridUtils';
import { createWorkingHoursRowFilter } from '../../../utils/workingHoursUtils';
import TableGridItem from '../../UI/TableGridItem';
import { FC, useMemo, useRef } from 'react';
import type { Dayjs } from '@utils/dayjs';
import { useBiddingCalculatedResults } from '../../../hooks/api/biddingCalculated';

interface ResultsTabPanelProps {
    selectedDate: Dayjs;
    selectedWorkingUnitLocation: number;
    allDatesInAMonth: Dayjs[];
    hideNonWorkingTime?: boolean;
    workingHours?: any[];
    publicHolidays?: any[];
}

const ResultsTabPanel: FC<ResultsTabPanelProps> = ({
    selectedDate,
    selectedWorkingUnitLocation,
    allDatesInAMonth,
    hideNonWorkingTime = true,
    workingHours,
    publicHolidays,
}) => {
    const gridRef = useRef(null);
    const { data: biddingCalculatedResults, isLoading } = useBiddingCalculatedResults({
        date: selectedDate,
        workingUnitLocation: selectedWorkingUnitLocation,
    });

    const tableRowsBiddingCalculatedResults = useMemo(() => {
        return biddingCalculatedResults.length > 0
            ? createTableRows(
                  createMapWithTableRows({
                      dates: allDatesInAMonth,
                      biddingCalculatedResults,
                      workingHours,
                      publicHolidays,
                  }),
                  'slotTime'
              )
            : [];
    }, [biddingCalculatedResults, allDatesInAMonth, workingHours, publicHolidays]);

    // Filter rows to hide non-working time slots if toggle is enabled
    const filteredRowData = useMemo(() => {
        const filterFn = createWorkingHoursRowFilter(allDatesInAMonth, workingHours, hideNonWorkingTime);
        return tableRowsBiddingCalculatedResults.filter(filterFn);
    }, [tableRowsBiddingCalculatedResults, hideNonWorkingTime, allDatesInAMonth, workingHours]);

    // Memoize column definitions to avoid re-creation on every render
    const columnDefs = useMemo(
        () =>
            createColumnDefs({
                allDatesInAMonth,
                valuePropertyName: 'value',
                showPercentageSign: true,
                cellStyleRenderer: ninetiethPercentileCellStyleRenderer,
            }),
        [allDatesInAMonth]
    );

    return (
        <TableGridItem
            ref={gridRef}
            rowData={filteredRowData}
            defaultColDef={defaultColumnDefs}
            columnDefs={columnDefs}
            columnHoverHighlight={true}
            loading={isLoading}
        />
    );
};

export default ResultsTabPanel;
